import React, { useState, useCallback } from 'react';
import { Frame, Navigation, TopBar } from '@shopify/polaris';
import {
  HomeIcon,
  ExportIcon,
  SettingsIcon,
  ClockIcon,
  ExitIcon,
  RefreshIcon,
  CreditCardIcon,
} from '@shopify/polaris-icons';
import { useAuth } from '../contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';

interface AppLayoutProps {
  children: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [userMenuActive, setUserMenuActive] = useState(false);

  const toggleUserMenuActive = useCallback(
    () => setUserMenuActive((active) => !active),
    [],
  );

  const navigationItems = [
    {
      label: 'Dashboard',
      icon: HomeIcon,
      url: '/',
      selected: location.pathname === '/',
    },
    {
      label: 'Tally Sync',
      icon: RefreshIcon,
      url: '/sync',
      selected: location.pathname === '/sync',
    },
    {
      label: 'Export Orders',
      icon: ExportIcon,
      url: '/export',
      selected: location.pathname === '/export',
    },
    {
      label: 'Export History',
      icon: ClockIcon,
      url: '/history',
      selected: location.pathname === '/history',
    },
    {
      label: 'Configuration',
      icon: SettingsIcon,
      url: '/configuration',
      selected: location.pathname === '/configuration',
    },
    {
      label: 'Billing',
      icon: CreditCardIcon,
      url: '/billing',
      selected: location.pathname === '/billing' || location.pathname === '/pricing',
    },
  ];

  const handleNavigationClick = (url: string) => {
    navigate(url);
  };

  const userMenuActions = [
    {
      items: [
        {
          content: 'Logout',
          icon: ExitIcon,
          onAction: logout,
        },
      ],
    },
  ];

  const topBarMarkup = (
    <TopBar
      showNavigationToggle
      userMenu={
        <TopBar.UserMenu
          actions={userMenuActions}
          name={user?.shop || 'Shop'}
          detail={user?.shop || ''}
          initials={(user?.shop || 'S').charAt(0).toUpperCase()}
          open={userMenuActive}
          onToggle={toggleUserMenuActive}
        />
      }
    />
  );

  const navigationMarkup = (
    <Navigation location={location.pathname}>
      <Navigation.Section
        items={navigationItems.map(item => ({
          ...item,
          onClick: () => handleNavigationClick(item.url),
        }))}
      />
    </Navigation>
  );

  return (
    <Frame
      topBar={topBarMarkup}
      navigation={navigationMarkup}
      showMobileNavigation={false}
    >
      {children}
    </Frame>
  );
};

export default AppLayout;